import React, { createContext, useContext, useEffect, useMemo } from "react";
import getSignalRService from "../services/signalRService";
import { getNotificationHubUrl } from "../utils/apiBaseUrl";
import { useParcelBookingNotifications } from "../hooks/useParcelBookingNotifications";
import { useCustomerSignalRNotifications } from "../hooks/useCustomerSignalRNotifications";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useIsCustomer } from "../hooks/useIsCustomer";
import { usesSignalRNotifications } from "../utils/jwtRole";

function logDevWebSocketGuide() {
  if (process.env.NODE_ENV !== "development") return;

  const hubUrl = getNotificationHubUrl();
  console.info(
    "%c[WebSockets — read this once]",
    "color:#c2410c;font-weight:bold;font-size:12px",
    "\n\n" +
      "❌ ws://localhost:3000/ws\n" +
      "   → React dev server (npm start). NOT notifications.\n\n" +
      "✅ Notifications (SignalR):\n" +
      `   → ${hubUrl || "(set REACT_APP_API_URL in .env)"}\n`,
  );
}

const SignalRContext = createContext(null);

export function SignalRProvider({ children }) {
  const service = useMemo(() => getSignalRService(), []);
  const isAdmin = useIsAdmin();
  const isCustomer = useIsCustomer();
  const signalREnabled = isAdmin || isCustomer;

  useEffect(() => {
    logDevWebSocketGuide();
  }, []);

  useParcelBookingNotifications(isAdmin);
  useCustomerSignalRNotifications(isCustomer);

  useEffect(() => {
    if (signalREnabled) {
      service.ensureBookingListenerRegistered();
    }
  }, [service, signalREnabled]);

  useEffect(() => {
    if (!usesSignalRNotifications()) {
      return undefined;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      service
        .start()
        .then(() => {
          if (!cancelled) {
            return service.syncRoleGroups();
          }
          return undefined;
        })
        .catch(() => {});
    };

    connect();

    const onOnline = () => {
      if (!cancelled && usesSignalRNotifications()) {
        service.attemptReconnect();
      }
    };

    window.addEventListener("online", onOnline);

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
    };
  }, [service, isAdmin, isCustomer]);

  return (
    <SignalRContext.Provider value={service}>{children}</SignalRContext.Provider>
  );
}

export function useSignalR() {
  const service = useContext(SignalRContext);
  if (!service) {
    throw new Error("useSignalR must be used within SignalRProvider");
  }
  return service;
}

export default SignalRProvider;
